import { createContext, useState, useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { authService } from "../service/auth/authService";
import { useMyAllowedOperators, useMyAllowedVehicles } from "../hooks/userComplementaryQuery";

const AuthContext = createContext(null);

/**
 * Componente interno que se monta DENTRO de AuthContext.Provider,
 * por lo que puede llamar useAuth() sin ciclo circular.
 * Se encarga de consultar y sincronizar allowedOperators / allowedVehicles.
 */
const AllowedDataSync = ({ setAllowedOperators, setAllowedVehicles }) => {
  const { user } = useAuth();

  const hasCuvLuv = useMemo(
    () => user?.roles?.some((r) => r.role_code === "CUV" || r.role_code === "LUV") ?? false,
    [user]
  );
  const managerId = hasCuvLuv ? user?.id : null;

  const { data: operatorsData } = useMyAllowedOperators(managerId);
  const { data: vehiclesData }  = useMyAllowedVehicles(managerId);

  useEffect(() => {
    if (!operatorsData) return;
    setAllowedOperators(operatorsData);
    localStorage.setItem("allowedOperators", JSON.stringify(operatorsData));
  }, [operatorsData, setAllowedOperators]);

  useEffect(() => {
    if (!vehiclesData) return;
    setAllowedVehicles(vehiclesData);
    localStorage.setItem("allowedVehicles", JSON.stringify(vehiclesData));
  }, [vehiclesData, setAllowedVehicles]);

  return null;
};

AllowedDataSync.propTypes = {
  setAllowedOperators: PropTypes.func.isRequired,
  setAllowedVehicles:  PropTypes.func.isRequired,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem("token");
  });

  // Estado para el rol activo seleccionado por el usuario
  const [selectedRole, setSelectedRole] = useState(() => {
    const savedRole = localStorage.getItem("selectedRole");
    return savedRole ? JSON.parse(savedRole) : null;
  });

  // Estado para controlar si se debe mostrar el selector de rol
  const [showRoleSelector, setShowRoleSelector] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // ─ Datos permitidos para gestores CUV/LUV ──────────────────────────────────────────────
  const [allowedOperators, setAllowedOperators] = useState(() => {
    try {
      const saved = localStorage.getItem("allowedOperators");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [allowedVehicles, setAllowedVehicles] = useState(() => {
    try {
      const saved = localStorage.getItem("allowedVehicles");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Determina si el usuario tiene rol CUV o LUV (independientemente del rol activo)
  const hasCuvLuvRole = useMemo(() => {
    if (!user?.roles) return false;
    return user.roles.some(
      (r) => r.role_code === "CUV" || r.role_code === "LUV"
    );
  }, [user]);

  const getSavedCredentials = () => {
    const savedCredentials = localStorage.getItem("savedCredentials");
    return savedCredentials ? JSON.parse(savedCredentials) : null;
  };

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      
      // Si el usuario tiene roles pero no hay rol seleccionado, determinar el rol por defecto
      if (user.roles && user.roles.length > 0 && !selectedRole) {
        const defaultRole = determineDefaultRole(user);
        setSelectedRole(defaultRole);
        localStorage.setItem("selectedRole", JSON.stringify(defaultRole));
      }
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("savedCredentials");
      localStorage.removeItem("selectedRole");
    }
  }, [token]);

  // Función para determinar el rol por defecto
  const determineDefaultRole = (userData) => {
    if (!userData.roles || userData.roles.length === 0) {
      return null;
    }

    // Si existe primary_role y está en el array de roles, usarlo
    if (userData.primary_role) {
      const primaryRoleExists = userData.roles.some(
        role => role.id === userData.primary_role.id
      );
      if (primaryRoleExists) {
        return userData.primary_role;
      }
    }

    // Sino, usar el primer rol del array
    return userData.roles[0];
  };

  // Función para cambiar el rol activo
  const changeRole = (newRole) => {
    if (!user || !user.roles) return;
    
    const roleExists = user.roles.some(role => role.id === newRole.id);
    if (roleExists) {
      setSelectedRole(newRole);
      localStorage.setItem("selectedRole", JSON.stringify(newRole));
      // Emitir evento para que otros componentes se enteren
      window.dispatchEvent(new CustomEvent('role:changed', { detail: newRole }));
    }
  };

  useEffect(() => {
    const handleTokenExpired = () => {
      console.warn("⚠️ Token expirado, cerrando sesión automáticamente");
      logout();
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    
    return () => {
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, []);

  const login = async (email, password, captchaToken, rememberUser = false) => {
    try {
      setIsLoading(true);
      const loginResponse = await authService.login(email, password, captchaToken);
      
      if (loginResponse.success) {
        setUser(loginResponse.user);
        setToken(loginResponse.token);
        
        // Determinar y establecer el rol por defecto
        if (loginResponse.user.roles && loginResponse.user.roles.length > 0) {
          const defaultRole = determineDefaultRole(loginResponse.user);
          setSelectedRole(defaultRole);
          localStorage.setItem("selectedRole", JSON.stringify(defaultRole));
          
          // Si tiene más de un rol, mostrar selector
          if (loginResponse.user.roles.length > 1) {
            setShowRoleSelector(true);
          }
        }
        
        if (rememberUser) {
          localStorage.setItem("savedCredentials", JSON.stringify({ email }));
        } else {
          localStorage.removeItem("savedCredentials");
        }
        
        return loginResponse;
      }
    } catch (error) {
      console.error("Error en el inicio de sesión:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Método simplificado para actualizar token (usado por socket)
  const updateToken = (newToken) => {
    setToken(newToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setSelectedRole(null);
    setShowRoleSelector(false);
    setAllowedOperators([]);
    setAllowedVehicles([]);
    localStorage.removeItem("allowedOperators");
    localStorage.removeItem("allowedVehicles");
    
    // Limpiar también la sesión de WSSP
    localStorage.removeItem('wssp_token');
    localStorage.removeItem('wssp_phone');
    
    // Emitir evento para que WsspAuthContext se entere
    window.dispatchEvent(new CustomEvent('auth:logout'));
  };

  const value = {
    user,
    token,
    selectedRole,
    showRoleSelector,
    setShowRoleSelector,
    allowedOperators,
    allowedVehicles,
    hasCuvLuvRole,
    login,
    logout,
    changeRole,
    updateToken,
    isLoading,
    getSavedCredentials
  };

  return (
    <AuthContext.Provider value={value}>
      <AllowedDataSync
        setAllowedOperators={setAllowedOperators}
        setAllowedVehicles={setAllowedVehicles}
      />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de AuthProvider");
  }
  return context;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};